// =========================================
// firebase/config.js — inizializzazione SDK Firebase (client)
// =========================================
//
// Legge la config della Web App dalle env `GATSBY_FIREBASE_*` (iniettate a
// build time da Gatsby). Se la config non è completa — o siamo lato server
// (SSR, niente window) — Firebase resta DISATTIVO e l'app usa il backend
// locale (localStorage). Così lo sviluppo su localhost continua a funzionare
// senza chiavi finché non le imposti (o non avvii gli emulatori).
//
// Per attivare Firebase in locale:
//   • progetto reale: compila GATSBY_FIREBASE_* in .env.development
//   • emulatori:      GATSBY_FIREBASE_USE_EMULATORS="true" (+ firebase emulators:start)

import { initializeApp, getApps, getApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';

const firebaseConfig = {
  apiKey:            process.env.GATSBY_FIREBASE_API_KEY,
  authDomain:        process.env.GATSBY_FIREBASE_AUTH_DOMAIN,
  projectId:         process.env.GATSBY_FIREBASE_PROJECT_ID,
  storageBucket:     process.env.GATSBY_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.GATSBY_FIREBASE_MESSAGING_SENDER_ID,
  appId:             process.env.GATSBY_FIREBASE_APP_ID,
};

const useEmulators = process.env.GATSBY_FIREBASE_USE_EMULATORS === 'true';

const hasWindow = () => typeof window !== 'undefined';

// Config valida = i campi indispensabili ci sono. Con gli emulatori bastano
// projectId + una apiKey fittizia (l'emulatore non valida le chiavi).
function hasConfig() {
  if (useEmulators) return !!firebaseConfig.projectId;
  return !!(firebaseConfig.apiKey && firebaseConfig.projectId && firebaseConfig.appId);
}

// Firebase è attivo solo lato client e con una config utilizzabile.
export function firebaseEnabled() {
  return hasWindow() && hasConfig();
}

let _app = null;
let _auth = null;
let _db = null;
let _emulatorsConnected = false;

function app() {
  if (_app) return _app;
  _app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  return _app;
}

function connectEmulatorsOnce() {
  if (_emulatorsConnected || !useEmulators) return;
  _emulatorsConnected = true;
  try { connectAuthEmulator(_auth, 'http://127.0.0.1:9099', { disableWarnings: true }); } catch { /* già connesso */ }
  try { connectFirestoreEmulator(_db, '127.0.0.1', 8080); } catch { /* già connesso */ }
}

export function auth() {
  if (!firebaseEnabled()) return null;
  if (!_auth) {
    _auth = getAuth(app());
    if (!_db) _db = getFirestore(app());
    connectEmulatorsOnce();
    // Login persistente via localStorage (sopravvive alla chiusura del browser),
    // come iotilize-dashboard. È il default di Firebase, ma lo impostiamo esplicito.
    setPersistence(_auth, browserLocalPersistence).catch(() => {});
  }
  return _auth;
}

export function db() {
  if (!firebaseEnabled()) return null;
  if (!_db) {
    _db = getFirestore(app());
    if (!_auth) _auth = getAuth(app());
    connectEmulatorsOnce();
  }
  return _db;
}
